import type { MembershipRole } from '@/store/types/MembershipRole'
import type { InvitationPayload } from '@/store/types/InvitationPayload'
import * as api from '@/backend/api'
import type { ParsedError } from '@/backend/error/types'
import { isErrorResponse } from '@/backend/error/types'
import { errorMessages, parseError } from '@/backend/error'

export type TeamInvitation = {
  email: string
  role: MembershipRole
}

/**
 * Add a new team invitation
 */
export const addInvitations = async (
  teamSlug: string,
  invitations: TeamInvitation[],
): Promise<InvitationPayload[] | ParsedError> => {
  let response
  try {
    response = await api.post<InvitationPayload[]>(`teams/${teamSlug}/invitations`, { invitations })
  } catch (error) {
    if (!isErrorResponse(error)) {
      throw error
    }
    return parseError(error, errorMessages.TEAM_MEMBERS_ADD_INVITATIONS)
  }

  return response.data
}

export const getPendingInvitations = async (
  teamSlug: string,
): Promise<InvitationPayload[] | ParsedError> => {
  let response
  try {
    response = await api.get<InvitationPayload[]>(`teams/${teamSlug}/invitations`, {
      confirmed: false,
    })
  } catch (error) {
    if (!isErrorResponse(error)) {
      throw error
    }
    return parseError(error, errorMessages.TEAM_MEMBERS_LOAD_INVITATIONS)
  }

  return response.data
}

export const deleteInvitation = async (
  teamId: number,
  invitationId: number,
): Promise<{ success: boolean } | ParsedError> => {
  let response
  try {
    response = await api.remove<{ success: boolean }>(`invitations/${invitationId}`)
  } catch (error) {
    if (!isErrorResponse(error)) {
      throw error
    }
    return parseError(error, errorMessages.TEAM_MEMBERS_DELETE_INVITATION, { teamId, invitationId })
  }

  return { success: response.data.success }
}

export const updateInvitation = async (
  invitationId: InvitationPayload['id'],
  role: MembershipRole,
): Promise<InvitationPayload | ParsedError> => {
  let response
  try {
    response = await api.put<InvitationPayload>(`invitations/${invitationId}`, { role })
  } catch (error) {
    if (!isErrorResponse(error)) {
      throw error
    }
    return parseError(error, errorMessages.TEAM_MEMBERS_UPDATE_INVITATION, { invitationId, role })
  }

  return response.data
}
